import { observer } from 'mobx-react-lite';
import { Dimensions } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  withTiming,
  useSharedValue,
  withSpring,
  runOnJS,
} from 'react-native-reanimated';
import { useEffect, useState } from 'react';
import { useStore } from 'src/contexts/StoreContext';
import { ToastProps } from './Toast.types';
import { TEXT_ICON_BUTTON_TYPES } from '../buttons/text-icon-button/TextIconButton.constants';
import { PanGestureHandler } from 'react-native-gesture-handler';
import { ToastContent } from './ToastContent';

const SCREEN_WIDTH = Dimensions.get('window').width;
const SWIPE_THRESHOLD = 80;

const Toast = observer(
  ({
    id,
    index,
    icon,
    iconColor,
    message,
    ctaText,
    onPress,
    alreadyExists,
    isExiting,
    group,
    type,
    loader,
    removeOnPress,
    ctaIconType = TEXT_ICON_BUTTON_TYPES.ICON_RIGHT,
    onPressClose,
    filled = false,
    animatedIcon,
    ctaIconName,
  }: ToastProps & { index: number }) => {
    const { toastStore } = useStore();
    const [isVisible, setIsVisible] = useState(false);
    const translateXSV = useSharedValue(SCREEN_WIDTH);

    useEffect(() => {
      if (!alreadyExists) {
        translateXSV.value = withSpring(
          0,
          {
            damping: 50,
            stiffness: 300,
            restDisplacementThreshold: 0.5,
            restSpeedThreshold: 0.5,
          },
          () => {
            animatedIcon && runOnJS(setIsVisible)(true);
          },
        );
      }
    }, [alreadyExists]);

    useEffect(() => {
      if (isExiting) {
        translateXSV.value = withTiming(
          Dimensions.get('window').width,
          {
            duration: 500,
            easing: Easing.ease,
          },
          () => {
            toastStore.removeToast(id);
          },
        );
      }
    }, [isExiting]);

    const translateX = useAnimatedStyle(() => {
      return {
        transform: [
          {
            translateX: translateXSV.value,
          },
        ],
      };
    });

    const onGestureEvent = (e: any) => {
      if (removeOnPress) translateXSV.value = e.nativeEvent.translationX;
    };

    const onGestureEnd = (e: any) => {
      if (removeOnPress) {
        const translationX = e.nativeEvent.translationX;
        if (Math.abs(translationX) > SWIPE_THRESHOLD) {
          const toValue = translationX > 0 ? SCREEN_WIDTH : -SCREEN_WIDTH;
          translateXSV.value = withTiming(toValue, { duration: 200 }, () => runOnJS(toastStore.removeToast)(id));
        } else {
          translateXSV.value = withSpring(0);
        }
      }
    };

    return (
      <PanGestureHandler onGestureEvent={onGestureEvent} onEnded={onGestureEnd}>
        <Animated.View style={translateX}>
          <ToastContent
            id={id}
            index={index}
            icon={icon}
            iconColor={iconColor}
            message={message}
            ctaText={ctaText}
            onPress={onPress}
            group={group}
            type={type}
            loader={loader}
            removeOnPress={removeOnPress}
            ctaIconType={ctaIconType}
            onPressClose={onPressClose}
            filled={filled}
            animatedIcon={animatedIcon}
            isAnimatedIconVisible={isVisible}
            ctaIconName={ctaIconName}
          />
        </Animated.View>
      </PanGestureHandler>
    );
  },
);

export default Toast;
